/**
 * Lecture d'un QR Code par la caméra.
 *
 * S'appuie sur `BarcodeDetector`, présent nativement sur Android — la plateforme
 * du terrain. Aucun décodeur JavaScript n'est embarqué : ce serait une trentaine
 * de kilo-octets imposés à tous pour un cas déjà couvert par le système, et la
 * **saisie manuelle** reste de toute façon nécessaire (QR déchiré, étiquette
 * effacée, téléphone sans caméra). Là où le détecteur manque, c'est donc elle
 * qui prend le relais, sans que l'application perde sa fonction.
 */

interface CodeDetecte {
  readonly rawValue: string;
}

interface DetecteurCodesBarres {
  detect(source: CanvasImageSource): Promise<readonly CodeDetecte[]>;
}

type FabriqueDetecteur = new (options?: { formats?: readonly string[] }) => DetecteurCodesBarres;

/**
 * Vrai si le système sait lire un QR Code sans aide.
 *
 * `mediaDevices` est déclaré non nul par les types du DOM mais reste absent hors
 * contexte sécurisé (HTTP en clair, WebView ancien) : le contrôle est fait sur
 * la valeur réelle, pas sur la déclaration.
 */
export function lectureCameraDisponible(): boolean {
  const mediaDevices = globalThis.navigator.mediaDevices as MediaDevices | undefined;
  return 'BarcodeDetector' in globalThis && typeof mediaDevices?.getUserMedia === 'function';
}

export interface SessionCamera {
  readonly flux: MediaStream;
  /** Arrête la caméra. **À appeler systématiquement** : sinon la diode reste allumée. */
  readonly arreter: () => void;
}

/**
 * Ouvre la caméra arrière.
 *
 * @throws {Error} si l'agent refuse l'autorisation ou si aucune caméra n'existe.
 * L'appelant traduit cela en invitation à saisir le numéro à la main.
 */
export async function ouvrirCamera(): Promise<SessionCamera> {
  const flux = await globalThis.navigator.mediaDevices.getUserMedia({
    // `environment` : la caméra arrière, celle qu'on pointe sur un document.
    video: { facingMode: 'environment' },
  });
  return {
    flux,
    arreter: () => {
      for (const piste of flux.getTracks()) piste.stop();
    },
  };
}

/**
 * Cherche un QR Code dans l'image courante de la vidéo.
 *
 * @returns Le contenu du premier code lu, ou `null` s'il n'y en a pas — cas
 * nominal, appelé plusieurs fois par seconde.
 */
export async function lireCode(video: HTMLVideoElement): Promise<string | null> {
  const fabrique = (globalThis as unknown as { BarcodeDetector?: FabriqueDetecteur })
    .BarcodeDetector;
  if (fabrique === undefined) return null;

  try {
    const detecteur = new fabrique({ formats: ['qr_code'] });
    const codes = await detecteur.detect(video);
    return codes[0]?.rawValue ?? null;
  } catch {
    // Image non exploitable à cet instant (vidéo pas encore prête, cadre flou).
    // Le prochain passage réessaiera : ce n'est pas une panne.
    return null;
  }
}
